import React, { useState } from "react";
import { useDocument } from "../loan_tool";
import { useUser } from "../../../hooks/user";
import { formatPhone, formatSSN, formatDOB } from "./form_utils";
import Image from "../../ui/image";
import "./css/ba_forms.scss";

export default function CreditAuth() {
  const [{ companyProfile }] = useUser();
  const [document, , { documentAccountProfile }] = useDocument();

  const [date, setDate] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [billingCityStateZip, setBillingCityStateZip] = useState("");
  const [cardNameOnCard, setCardNameOnCard] = useState("");
  const [cardType, setCardType] = useState("");
  const [cardExp, setCardExp] = useState("");
  const [cardNum1, setCardNum1] = useState("");
  const [cardNum2, setCardNum2] = useState("");
  const [cardNum3, setCardNum3] = useState("");
  const [cardNum4, setCardNum4] = useState("");
  const [cardSC, setCardSC] = useState("");
  const [sigDate, setSigDate] = useState("");

  const borrowers = (document.applicant && document.applicant.borrowers) || [];
  const b1 = borrowers[0] || {};
  const b2 = borrowers[1] || {};

  const loName = [documentAccountProfile.first_name, documentAccountProfile.last_name]
    .filter(Boolean).join(" ");

  const companyName = companyProfile.company_name || "";

  const companyAddr = [
    companyProfile.business_address,
    companyProfile.business_address_2
  ].filter(Boolean).join(" ");

  const companyCityLine = [companyProfile.city, companyProfile.state]
    .filter(Boolean).join(", ") + (companyProfile.zip ? " " + companyProfile.zip : "");

  function fullName(b) {
    return [b.first_name, b.middle_name, b.last_name].filter(Boolean).join(" ");
  }

  function b1Addr() {
    return [b1.current_address, b1.current_address_2].filter(Boolean).join(" ");
  }

  function b1CityStateZip() {
    return [b1.city, b1.state].filter(Boolean).join(", ") +
      (b1.zip_code ? " " + b1.zip_code : "");
  }

  function b2Addr() {
    return [b2.current_address, b2.current_address_2].filter(Boolean).join(" ");
  }

  function b2CityStateZip() {
    return [b2.city, b2.state].filter(Boolean).join(", ") +
      (b2.zip_code ? " " + b2.zip_code : "");
  }

  return (
    <div className="ba-doc ca-doc">
      {/* Header: logo left, company/LO info right */}
      <div className="ca-letterhead">
        <div className="ca-letterhead-logo">
          <Image url="company/company-logo" className="ca-logo-img" />
        </div>
        <div className="ca-letterhead-info">
          {companyName && (
            <div className="ca-letterhead-company">
              {companyName}
              {companyProfile.company_state_license && `, LIC# ${companyProfile.company_state_license}`}
              {companyProfile.company_nmls_number && `, NMLS# ${companyProfile.company_nmls_number}`}
            </div>
          )}
          {loName && (
            <div className="ca-letterhead-lo">
              Originator: {loName}
              {documentAccountProfile.state_real_estate_license && `, LIC# ${documentAccountProfile.state_real_estate_license}`}
              {documentAccountProfile.nmls_number && `, NMLS# ${documentAccountProfile.nmls_number}`}
            </div>
          )}
        </div>
      </div>
      <div className="ca-letterhead-rule" />

      <div className="ba-doc-title">Credit Authorization for Loan Application</div>

      {/* Authorization text */}
      <p className="ca-body-text">
        Authorization is hereby granted to obtain information regarding my employment, checking and / or savings
        accounts, any outstanding credit accounts or mortgage loans. Please use this form as our intent to proceed
        with the loan presented. Authorization is hereby granted to use a photostatic copy of my signature below
        to obtain information regarding any of the aforementioned items.
      </p>

      <p className="ca-body-text">
        If for any reason this application is canceled by the borrower, the borrower agrees to pay for credit
        fees, appraisals and any other costs that{companyName ? <strong> {companyName} </strong> : " the lender "}
        assumes during the process.
      </p>

      {/* Cost section */}
      <div className="ca-cost-box">
        <p className="ca-cost-line">
          <strong>Cost of Credit Report:</strong>&nbsp; Married Couple &amp; Single applicant Tri-Merged Report{" "}
          <strong>$150.00*</strong>
        </p>
        <p className="ca-cost-note">
          *NOTE: The Lender may pull the same report during the underwriting process resulting in additional charges.
          The $150.00 fee is an <em>approximate</em> charge.
        </p>
        <p className="ca-cost-subhead"><strong>Costs which may apply:</strong></p>
        <p className="ca-cost-note">
          If your home is a Condominium or a PUD, a HOA Cert and Condo Cert may be required and the cost will
          range from $200 to $350 and would be required for full loan approval.
        </p>
        <p className="ca-cost-note">
          If your employer uses a 3rd party to verify employment an additional fee may range from $50 – $100.
        </p>
      </div>

      {/* Date */}
      <div className="ca-date-row">
        <span className="ca-field-label">Date:</span>
        <input
          className="ca-inline-input ca-date-input"
          type="text"
          placeholder="MM/DD/YYYY"
          value={date}
          onChange={e => setDate(e.target.value)}
        />
      </div>

      {/* Two-column borrower section */}
      <table className="ca-borrower-table">
        <colgroup>
          <col style={{ width: "50%" }} />
          <col style={{ width: "50%" }} />
        </colgroup>
        <tbody>
          {/* Print Names */}
          <tr>
            <td className="ca-td">
              <div className="ca-col-label">Primary Borrower — Print Full Legal Name</div>
              <div className="ca-auto-field">{fullName(b1)}</div>
              <div className="ca-sub-label">(First, Middle, Last)</div>
            </td>
            <td className="ca-td">
              <div className="ca-col-label">Co-Borrower — Print Full Legal Name</div>
              <div className="ca-auto-field">{fullName(b2)}</div>
              <div className="ca-sub-label">(First, Middle, Last)</div>
            </td>
          </tr>

          {/* Signatures */}
          <tr>
            <td className="ca-td ca-sig-td">
              <div className="ca-sig-line"></div>
              <div className="ca-col-label">Primary Borrower Signature</div>
            </td>
            <td className="ca-td ca-sig-td">
              <div className="ca-sig-line"></div>
              <div className="ca-col-label">Co-Borrower Signature</div>
            </td>
          </tr>

          {/* SSN */}
          <tr>
            <td className="ca-td">
              <div className="ca-col-label">Social Security Number</div>
              <div className="ca-auto-field">{formatSSN(b1.ssn)}</div>
            </td>
            <td className="ca-td">
              <div className="ca-col-label">Social Security Number</div>
              <div className="ca-auto-field">{formatSSN(b2.ssn)}</div>
            </td>
          </tr>

          {/* DOB */}
          <tr>
            <td className="ca-td">
              <div className="ca-col-label">Date of Birth</div>
              <div className="ca-auto-field">{formatDOB(b1.date_of_birth)}</div>
            </td>
            <td className="ca-td">
              <div className="ca-col-label">Date of Birth</div>
              <div className="ca-auto-field">{b2.date_of_birth}</div>
            </td>
          </tr>

          {/* Address */}
          <tr>
            <td className="ca-td">
              <div className="ca-col-label">Address</div>
              <div className="ca-auto-field">{b1Addr()}</div>
            </td>
            <td className="ca-td">
              <div className="ca-col-label">Address</div>
              <div className="ca-auto-field">{b2Addr()}</div>
            </td>
          </tr>

          {/* City / State / Zip */}
          <tr>
            <td className="ca-td">
              <div className="ca-col-label">City / State / Zip</div>
              <div className="ca-auto-field">{b1CityStateZip()}</div>
            </td>
            <td className="ca-td">
              <div className="ca-col-label">City / State / Zip</div>
              <div className="ca-auto-field">{b2CityStateZip()}</div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Credit Card Section */}
      <div className="ca-section-heading">Credit Card Authorization for Appraisal and Credit Report</div>

      <div className="ca-cc-grid">
        <div className="ca-cc-field ca-cc-full">
          <span className="ca-field-label">Name as it appears on the Credit Card:</span>
          <input
            className="ca-inline-input ca-cc-name-input"
            type="text"
            placeholder="Enter cardholder name"
            value={cardNameOnCard}
            onChange={e => setCardNameOnCard(e.target.value)}
          />
        </div>

        <div className="ca-cc-field">
          <span className="ca-field-label">Billing Address:</span>
          <input
            className="ca-inline-input"
            type="text"
            placeholder="Street address"
            value={billingAddress}
            onChange={e => setBillingAddress(e.target.value)}
          />
        </div>

        <div className="ca-cc-field">
          <span className="ca-field-label">City, State, Zip:</span>
          <input
            className="ca-inline-input"
            type="text"
            placeholder="City, State Zip"
            value={billingCityStateZip}
            onChange={e => setBillingCityStateZip(e.target.value)}
          />
        </div>

        <div className="ca-cc-field">
          <span className="ca-field-label">Type of Card:</span>
          <label className="ca-checkbox-label">
            <input
              type="radio"
              name="cardType"
              value="VISA"
              checked={cardType === "VISA"}
              onChange={() => setCardType("VISA")}
            />
            {" "}VISA
          </label>
          <label className="ca-checkbox-label" style={{ marginLeft: "16px" }}>
            <input
              type="radio"
              name="cardType"
              value="MC"
              checked={cardType === "MC"}
              onChange={() => setCardType("MC")}
            />
            {" "}MasterCard
          </label>
        </div>

        <div className="ca-cc-field">
          <span className="ca-field-label">Expiration Date (MM/YY):</span>
          <input
            className="ca-inline-input ca-exp-input"
            type="text"
            placeholder="MM/YY"
            value={cardExp}
            onChange={e => setCardExp(e.target.value)}
          />
        </div>

        <div className="ca-cc-field ca-cc-full">
          <span className="ca-field-label">Account Number:</span>
          <div className="ca-card-number-group">
            {[
              [cardNum1, setCardNum1],
              [cardNum2, setCardNum2],
              [cardNum3, setCardNum3],
              [cardNum4, setCardNum4]
            ].map(([val, setter], i) => (
              <input
                key={i}
                className="ca-inline-input ca-card-seg"
                type="text"
                maxLength={4}
                placeholder="XXXX"
                value={val}
                onChange={e => setter(e.target.value)}
              />
            ))}
            <span className="ca-field-label" style={{ marginLeft: "16px" }}>SC:</span>
            <input
              className="ca-inline-input ca-sc-input"
              type="text"
              maxLength={4}
              placeholder="XXX"
              value={cardSC}
              onChange={e => setCardSC(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Appraisal email + sig */}
      <div className="ca-appraisal-row">
        <span className="ca-field-label">Please email the Appraisal report to:</span>
        <span className="ca-auto-value">{b1.email || ""}</span>
      </div>

      <div className="ca-final-sig-row">
        <div className="ca-final-sig-left">
          <div className="ca-sig-line"></div>
          <div className="ca-col-label">Borrower Signature — Authorized to Use This Card for Appraisal</div>
        </div>
        <div className="ca-final-sig-right">
          <input
            className="ca-inline-input"
            type="text"
            placeholder="MM/DD/YYYY"
            value={sigDate}
            onChange={e => setSigDate(e.target.value)}
          />
          <div className="ca-col-label">Date (MM/DD/YYYY)</div>
        </div>
      </div>

      {/* Company footer */}
      {(companyName || companyAddr) && (
        <div className="ca-company-footer">
          {companyName && <span>{companyName}</span>}
          {companyAddr && <span> · {companyAddr}</span>}
          {companyCityLine && <span> · {companyCityLine}</span>}
          {companyProfile.contact_phone && (
            <span> · TEL: {formatPhone(companyProfile.contact_phone)}</span>
          )}
        </div>
      )}

      <div className="ba-form-footer">LANIS Form - (07/01/2026)</div>
    </div>
  );
}
